/**
 * SDA-299: page-visit log.
 *
 * One fire-and-forget POST per page change to /api/track/page-view. Sends the
 * pathname only (never the query string), a random per-tab session id, and
 * the campaign/contact from the stored campaign attribution (lib/attribution).
 * The server decides what is stored; this never throws and is never awaited.
 */

import { getAttribution } from './attribution';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const SESSION_KEY = 'sda_page_session';

let memorySessionId: string | null = null;

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

/** Random id for this browser tab; kept in sessionStorage, in memory if storage is blocked. */
export function getSessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = newId();
    window.sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    if (!memorySessionId) memorySessionId = newId();
    return memorySessionId;
  }
}

export function trackPageView(pathname: string, token?: string | null): void {
  try {
    const attribution = getAttribution();
    const body = {
      path: pathname,
      session_id: getSessionId(),
      ...(attribution?.campaign_id ? { campaign_id: attribution.campaign_id } : {}),
      ...(attribution?.contact_id ? { contact_id: attribution.contact_id } : {}),
    };
    fetch(`${API_BASE_URL}/api/track/page-view`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch {
    // Tracking is best-effort only.
  }
}
